import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ToastrModule } from 'ngx-toastr';

import { AppRoutingModule } from './app-routing.module';

import { ExternalLinkService, GlobalErrorHandlerService, ZoningCodeService, SyncService } from './core/services';

import { AppComponent } from './app.component';
import { AppViewComponent } from './draft-page/app-view/app-view.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { ZonesUsesComponent } from './draft-page/zones-uses/zones-uses.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { httpInterceptorProviders } from './core/interceptors';
import { LoaderService } from '../shared/services';
import { CookieService } from 'ngx-cookie-service';
import { OverlayModule } from '@angular/cdk/overlay';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { CurrentZoningCodeComponent } from './components/current-zoning-code/current-zoning-code.component';

import { AccordionComponent } from './draft-page/accordion/accordion.component';
import { AccordionGroupComponent } from './draft-page/accordion/accordion-group/accordion-group.component';
import { UseTypeTableComponent } from './draft-page/zones-uses/use-type-table/use-type-table.component';
import { AuthenticationService } from 'src/shared/auth';

import { ZoneIQUIModule } from '../shared/zoneiqui/zoneiq-ui.module';
import { ZIQFooterComponent } from './components/ziq-footer/ziq-footer.component';
import { ZonesTableComponent } from './draft-page/zones-table/zones-table.component';
import { ActiveConnectedUsersBtnComponent } from './components/user-menu/active-connected-users-btn/active-connected-users-btn.component';
import { ActiveConnectedUsersPopupComponent } from './components/user-menu/active-connected-users-btn/active-connected-users-popup/active-connected-users-popup.component';
import { ShowHideHoverDirective } from '../shared/directives/show-hide-hover.directive';
import { ImportComponent } from './import-page/import/import.component';
import { UsesDataTableComponent } from './import-page/import/data-table/data-table.component';
import { ImportButtonComponent } from './import-page/import-button/import-button.component';
import { DataImportComponent } from './import-page/data-import/data-import.component';
import { EditUsesPopupComponent } from './import-page/import/data-table/edit-uses-popup/edit-uses-popup.component';
import { ZonesBuildingTypologyComponent } from './draft-page/zones-building-typology/zones-building-typology.component';
import { OverlayTypologyComponent } from './draft-page/overlay-typology/overlay-typology.component';
import { ZoningCodeChangesPopupComponent } from './components/user-menu/zoning-code-changes-btn/zoning-code-changes-popup/zoning-code-changes-popup.component';
import { ZoningCodeChangesBtnComponent } from './components/user-menu/zoning-code-changes-btn/zoning-code-changes-btn.component';
import { UsesChangesTableComponent } from './components/user-menu/zoning-code-changes-btn/zoning-code-changes-tables/uses-changes-table/uses-changes-table.component';
import { OverlayTypologyChangesTableComponent } from './components/user-menu/zoning-code-changes-btn/zoning-code-changes-tables/overlay-typology-changes-table/overlay-typology-changes-table.component';
import { ZoneCombinationComponent } from './components/zone-combination/zone-combination/zone-combination.component';
import { CalibrationSetTableFieldAttributesComponent } from './components/attributes/calibration-set-table-field-attributes.component';
import { NumberValueFieldComponent } from './components/fields/number-value-field/number-value-field.component';
import { HorizontalScrollTableComponent } from './components/table/horizontal-scroll-table/horizontal-scroll-table.component';
import { ZIQUseAllowanceSelectComponent } from './components/calibrations/uses/ziq-use-allowance-select/ziq-use-allowance-select.component';
import { ZIQUsesCellComponent } from './components/calibrations/uses/ziq-uses-cell/ziq-uses-cell.component';
import { ZIQUsesVisibleFieldsComponent } from './components/calibrations/uses/ziq-uses-visible-fields/ziq-uses-visible-fields.component';
import { BooleanValueFieldComponent } from './components/fields/boolean-value-field/boolean-value-field.component';
import { HeaderFieldComponent } from './components/table/header-field/header-field.component';
import { CalibrationSetAttributesComponent } from './components/attributes/calibration-set-attributes/calibration-set-attributes.component';
import { CalibrationSetAttributesPopupComponent } from './components/attributes/attributes-popup/calibration-set-attributes-popup/calibration-set-attributes-popup.component';
import { CalibrationSetTableFieldAttributesPopupComponent } from './components/attributes/attributes-popup/calibration-set-table-field-attributes-popup.component';
import { ZoningCodeAttributesPopupComponent } from './components/attributes/attributes-popup/zoning-code-attributes-popup.component';
import { AttributesComponent } from './components/attributes/attributes.component';
import { ZoningCodeAttributesComponent } from './components/attributes/zoning-code-attributes.component';
import { CalibrationUsesLabelAttributesComponent } from './components/attributes/calibration-uses-label-attributes.component';
import { AttributesPopupComponent } from './components/attributes/attributes-popup/attributes-popup.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { EntityAttributeIconComponent } from './components/attributes/entity-attribute-icon/entity-attribute-icon.component';
import { DialogImportDataComponent } from './import-page/import-button/import-data-dialog/import-data-dialog.component';
import { AssumptionComponentComponent } from './components/attributes/assumption-component/assumption-component.component';
import { CalibrationSetAttributesIconsComponent } from './components/attributes/calibration-set-attributes-icons/calibration-set-attributes-icons.component';
import { OverlayTypologyAttributesComponent } from './components/attributes/overlay-typology-attributes.component';
import { NumberNaValueFieldComponent } from './components/fields/number-na-value-field/number-na-value-field.component';
import { FieldLevelNaButtonComponent } from './components/table/field-level-na-button/field-level-na-button.component';
import { TableWithSectionsComponent } from './components/table/table-with-sections/table-with-sections.component';
import { ZCValidationIconComponent } from './components/zc-validation/zc-validation-icon/zc-validation-icon.component';
import { ZCValidationResultComponent } from './components/zc-validation/zc-validation-result/zc-validation-result.component';
import { ZcValidationResultTableComponent } from './components/zc-validation/zc-validation-result-table/zc-validation-result-table.component';
import { QuillModule } from 'ngx-quill';
import { NotificationMessageService } from './core/services/notification-message.service';
import { CalibrationAdminPageComponent } from './admin-page/calibration-admin-page/calibration-admin-page.component';
import { IsNotCalibratedReasonsManagementPageComponent } from './admin-page/is-not-calibrated-reasons-management-page/is-not-calibrated-reasons-management-page.component';
import { GridicsUsesTagsManagementPageComponent } from './admin-page/gridics-uses-tags-management-page/gridics-uses-tags-management-page.component';
import { IsNotCalibratedReasonService } from './core/services/is-not-calibrated-reason.service';
import { GridicsUseTagService } from './core/services/gridics-use-tag.service';
import { IncrEditPopupComponent } from './admin-page/is-not-calibrated-reasons-management-page/incr-edit-popup/incr-edit-popup.component';
import { GridicsUseTagEditPopupComponent } from './admin-page/gridics-uses-tags-management-page/gridics-use-tag-edit-popup/gridics-use-tag-edit-popup.component';
import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { ZcXlsxExportButtonComponent } from './components/zc-export/zc-xlsx-export-button/zc-xlsx-export-button.component';
import { CalibrationUsesValuesImportTableComponent } from './import-page/calibration-uses-values-import-table/calibration-uses-values-import-table.component';
import { UploadFileDialogComponent } from './import-page/upload-file-dialog/upload-file-dialog.component';
import { ZoningCodesImportTableComponent } from './import-page/zoning-codes-import-table/zoning-codes-import-table.component';
import { ZoningCodesImportTabComponent } from './import-page/zoning-codes-import-tab/zoning-codes-import-tab.component';
import { DownloadZoningCodeImportTemplateComponent } from './import-page/download-zoning-code-import-template/download-zoning-code-import-template.component';
import { ImportFooterComponent } from './import-page/import-footer/import-footer.component';
import { CalibrationTabularImportTableComponent } from './import-page/calibration-tabular-import-table/calibration-tabular-import-table.component';
import { ZoneDescriptionsImportTableComponent } from './import-page/zone-descriptions-import-table/zone-descriptions-import-table.component';
import { CalibrationSetTableFieldAttributesImportTableComponent } from './import-page/calibration-set-table-field-attributes-import-table/calibration-set-table-field-attributes-import-table.component';
import { CalibrationUsesLabelAttributesPopupComponent } from './components/attributes/attributes-popup/calibration-uses-label-attributes-popup/calibration-uses-label-attributes-popup.component';

@NgModule({ declarations: [
        ShowHideHoverDirective,
        AppComponent,
        AppViewComponent,
        NotFoundComponent,
        ImportComponent,
        ZonesUsesComponent,
        UserMenuComponent,
        UserMenuComponent,
        CurrentZoningCodeComponent,
        AccordionComponent,
        AccordionGroupComponent,
        UseTypeTableComponent,
        ZIQFooterComponent,
        ZonesTableComponent,
        ActiveConnectedUsersBtnComponent,
        ActiveConnectedUsersPopupComponent,
        ImportButtonComponent,
        UsesDataTableComponent,
        DataImportComponent,
        EditUsesPopupComponent,
        ZonesBuildingTypologyComponent,
        OverlayTypologyComponent,
        ZoningCodeChangesPopupComponent,
        ZoningCodeChangesBtnComponent,
        UsesChangesTableComponent,
        OverlayTypologyChangesTableComponent,
        ZoneCombinationComponent,
        CalibrationSetTableFieldAttributesComponent,
        NumberValueFieldComponent,
        HorizontalScrollTableComponent,
        ZIQUseAllowanceSelectComponent,
        ZIQUsesCellComponent,
        ZIQUsesVisibleFieldsComponent,
        BooleanValueFieldComponent,
        HeaderFieldComponent,
        CalibrationSetAttributesComponent,
        CalibrationSetAttributesPopupComponent,
        CalibrationSetTableFieldAttributesPopupComponent,
        ZoningCodeAttributesPopupComponent,
        AttributesComponent,
        ZoningCodeAttributesComponent,
        AttributesPopupComponent,
        EntityAttributeIconComponent,
        DialogImportDataComponent,
        AssumptionComponentComponent,
        CalibrationSetAttributesIconsComponent,
        OverlayTypologyAttributesComponent,
        NumberNaValueFieldComponent,
        FieldLevelNaButtonComponent,
        TableWithSectionsComponent,
        ZCValidationIconComponent,
        ZCValidationResultComponent,
        ZcValidationResultTableComponent,
        CalibrationAdminPageComponent,
        IsNotCalibratedReasonsManagementPageComponent,
        GridicsUsesTagsManagementPageComponent,
        IncrEditPopupComponent,
        GridicsUseTagEditPopupComponent,
        AccessDeniedComponent,
        ZcXlsxExportButtonComponent,
        CalibrationUsesValuesImportTableComponent,
        UploadFileDialogComponent,
        ZoningCodesImportTableComponent,
        ZoningCodesImportTabComponent,
        DownloadZoningCodeImportTemplateComponent,
        ImportFooterComponent,
        CalibrationTabularImportTableComponent,
        ZoneDescriptionsImportTableComponent,
        CalibrationSetTableFieldAttributesImportTableComponent,
        CalibrationUsesLabelAttributesComponent,
        CalibrationUsesLabelAttributesPopupComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        ZoneIQUIModule,
        FormsModule,
        ScrollingModule,
        OverlayModule,
        FormsModule,
        ReactiveFormsModule,
        ToastrModule.forRoot({
            timeOut: 5000,
            positionClass: 'toast-top-right',
            preventDuplicates: true
        }),
        MatSlideToggleModule,
        MatFormFieldModule,
        QuillModule.forRoot(),
        MatListModule,
        DragDropModule], providers: [
        GlobalErrorHandlerService,
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
        CookieService,
        AuthenticationService,
        ExternalLinkService,
        httpInterceptorProviders,
        ZoningCodeService,
        SyncService,
        LoaderService,
        NotificationMessageService,
        IsNotCalibratedReasonService,
        GridicsUseTagService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}
